import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { useAuthContext } from "../../../../../components/Auth";
import { AssigneePicker } from "../../../../../components/AssigneePopOver";
import type { AssigneeOption } from "../../../../../components/AssigneePopOver";
import type { ApprovalWorkflow } from "../../../../../types/types.Workflow";
import { useApprovalMutations } from "../hooks/useApprovalMutations";
import {
  approvalAssigneeDisabledTooltip,
  canEditApprovalAssignee,
  findActiveApprovalStage,
  toAssigneeOptions,
} from "../utils";

const CellWrapper = styled.div`
  min-width: 160px;
`;

type ApprovalsListAssigneeCellProps = {
  approval: ApprovalWorkflow;
  onAssigneeUpdated?: () => unknown;
};

/**
 * Interactive assignee control for the approvals list table.
 * Mirrors ApprovalDetailPage permissions + AssigneePicker wiring.
 */
export function ApprovalsListAssigneeCell({
  approval,
  onAssigneeUpdated,
}: ApprovalsListAssigneeCellProps) {
  const { t } = useTranslation();
  const { user, roleIsAdmin, roleIsAgilisAdmin } = useAuthContext();
  const { updateAssignee } = useApprovalMutations();
  const [optimisticAssignee, setOptimisticAssignee] =
    useState<AssigneeOption | null | undefined>(undefined);

  const currentStage = findActiveApprovalStage(
    approval.stages ?? [],
    approval.current_stage
  );
  const assignee = currentStage?.assignee ?? null;

  const selectedAssignee: AssigneeOption | null = assignee
    ? {
        id: String(assignee.id),
        name: assignee.name,
        email: assignee.email,
      }
    : null;

  useEffect(() => {
    setOptimisticAssignee(undefined);
  }, [assignee?.id, assignee?.name]);

  const displayAssignee =
    optimisticAssignee !== undefined ? optimisticAssignee : selectedAssignee;

  const options = useMemo(
    () => toAssigneeOptions(currentStage?.approvers ?? []),
    [currentStage?.approvers]
  );

  const canEditAssignee = canEditApprovalAssignee(approval, {
    userId: user?.id,
    roleIsAdmin,
    roleIsAgilisAdmin,
  });

  const disabledTooltip = approvalAssigneeDisabledTooltip(t, {
    approval,
    canEdit: canEditAssignee,
    assigneeName: displayAssignee?.name,
  });

  const handleSelect = async (option: AssigneeOption | null) => {
    setOptimisticAssignee(option);
    try {
      await updateAssignee(approval.id, {
        assignee_id: option?.id ?? null,
      });
      void onAssigneeUpdated?.();
    } catch {
      setOptimisticAssignee(undefined);
      // Error toast handled in useApprovalMutations
    }
  };

  return (
    <CellWrapper
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <AssigneePicker
        name={displayAssignee?.name}
        selectedId={displayAssignee?.id}
        stageName={currentStage?.display_name || currentStage?.name || ""}
        options={options}
        disabled={!canEditAssignee}
        disabledTooltip={disabledTooltip}
        unassignedLabel={t("Unassigned")}
        onSelect={handleSelect}
        popoverProps={{
          title: t("Assignee"),
          searchPlaceholder: t("Search"),
          stageLabelSuffix: t("review"),
        }}
      />
    </CellWrapper>
  );
}
